import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  variant?: ToastVariant;
  /** Auto-dismiss after this many ms (default 5000; errors 8000). 0 keeps it until dismissed. */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  durationMs: number;
}

export interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
  dismissToast: (id: number) => void;
}

const noop = () => undefined;
const ToastContext = createContext<ToastContextValue>({ showToast: noop, dismissToast: noop });

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'bg-green-50 border-green-300 text-green-800',
  error: 'bg-red-50 border-red-300 text-red-800',
  warning: 'bg-amber-50 border-amber-300 text-amber-800',
  info: 'bg-slate-50 border-slate-300 text-slate-800',
};

const VARIANT_ICON: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle size={16} aria-hidden="true" />,
  error: <AlertTriangle size={16} aria-hidden="true" />,
  warning: <AlertTriangle size={16} aria-hidden="true" />,
  info: <Info size={16} aria-hidden="true" />,
};

const ToastView: React.FC<{ toast: ToastItem; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (toast.durationMs <= 0) return undefined;
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-md ${VARIANT_STYLES[toast.variant]}`}
    >
      <span className="mt-0.5 flex-shrink-0">{VARIANT_ICON[toast.variant]}</span>
      <span className="flex-1 text-left">{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
        aria-label="Dismiss notification"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

/** Mount once near the app root. Components call `useToast().showToast(...)`. */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, options: ToastOptions = {}) => {
    const variant = options.variant ?? 'info';
    const durationMs = options.durationMs ?? (variant === 'error' ? 8000 : 5000);
    const id = nextId.current++;
    // Keep at most 4 on screen
    setToasts((prev) => [...prev.slice(-3), { id, message, variant, durationMs }]);
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 left-4 sm:left-auto z-[60] flex flex-col gap-2 sm:w-96"
      >
        {toasts.map((toast) => (
          <ToastView key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/** Toast API. Outside a ToastProvider the calls do nothing (safe in isolated tests). */
export const useToast = (): ToastContextValue => useContext(ToastContext);

export default ToastProvider;
